import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getActiveUserPhoneOtpList } from "./helper/userPhoneOtpApi";

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const UserPhoneOtpPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  const fetchList = useCallback(async () => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const result = await getActiveUserPhoneOtpList();

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching active user phone OTP list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">User ID</span>,
        selector: (row) => row.user_id,
        sortable: true,
        cell: (row) => (
          <span className="font-monospace small">{row.user_id || "-"}</span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Username</span>,
        selector: (row) => row.user?.username,
        sortable: true,
        cell: (row) => row.user?.username || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">Phone (last 4)</span>,
        selector: (row) => row.phone_last4,
        sortable: true,
        cell: (row) => row.phone_last4 || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">OTP</span>,
        selector: (row) => row.otp,
        sortable: true,
        cell: (row) => (
          <span className="font-monospace fw-semibold">{row.otp || "-"}</span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Channel</span>,
        selector: (row) => row.channel,
        sortable: true,
        cell: (row) => row.channel || "-",
      },
      {
        name: <span className="font-weight-bold fs-13">Expires At</span>,
        selector: (row) => row.expires_at,
        sortable: true,
        cell: (row) => formatDateTime(row.expires_at),
      },
      {
        name: <span className="font-weight-bold fs-13">Last Updated</span>,
        selector: (row) => row.updated_at,
        sortable: true,
        cell: (row) => formatDateTime(row.updated_at),
      },
      {
        name: <span className="font-weight-bold fs-13">Attempts</span>,
        selector: (row) => row.attempt_count,
        sortable: true,
        cell: (row) => row.attempt_count ?? 0,
      },
      {
        name: <span className="font-weight-bold fs-13">Status</span>,
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => (
          <Badge color="warning" className="font-size-12">
            {row.status || "pending"}
          </Badge>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchList]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="User Phone OTP"
            breadcrumbItem="User Phone OTP"
          />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-cellphone-key me-2"></i>
                    Active User Phone OTPs
                  </h4>
                  <Button color="info" size="sm" onClick={fetchList} disabled={loading}>
                    Refresh
                  </Button>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer={false}
                    paginationPerPage={50}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i
                          className="mdi mdi-cellphone-off"
                          style={{ fontSize: "3rem", color: "#6c757d" }}
                        ></i>
                        <p className="mt-3 text-muted">
                          No active user phone OTPs (non-expired) found
                        </p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserPhoneOtpPage;
